import { Box, Button, Typography, IconButton, Fade } from '@mui/material'
import { Close, Logout, PersonAdd } from '@mui/icons-material'
import { useState } from 'react'

interface GuestBannerProps {
  onSignIn: () => void
  onRegister: () => void
  onDismiss?: () => void
  autoSaveEnabled?: boolean
}

/**
 * Banner shown to guest users in the pattern editor
 * Encourages them to sign up to save their work
 */
export default function GuestBanner({
  onSignIn,
  onRegister,
  onDismiss,
  autoSaveEnabled = true,
}: GuestBannerProps) {
  const [visible, setVisible] = useState(true)

  const handleDismiss = () => {
    setVisible(false)
    onDismiss?.()
  }

  if (!visible) return null

  return (
    <Fade in={visible}>
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 24px',
            maxWidth: '100%',
            margin: '0 auto',
            gap: 2,
          }}
        >
          {/* Left side - Icon and Message */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
            {/* Icon */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: '22px',
              }}
            >
              🎉
            </Box>

            {/* Text content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  marginBottom: '2px',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                }}
              >
                You're creating as a guest
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '13px',
                  fontWeight: 400,
                  lineHeight: 1.4,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                }}
              >
                {autoSaveEnabled
                  ? 'Your design is auto-saved locally. Sign up to save permanently and access from anywhere.'
                  : 'Sign up to save your design and access it from anywhere.'}
              </Typography>
            </Box>
          </Box>

          {/* Right side - Action buttons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            {/* Sign In button */}
            <Button
              onClick={onSignIn}
              startIcon={<Logout sx={{ fontSize: 16 }} />}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'none',
                padding: '6px 16px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease',
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              Sign In
            </Button>

            {/* Register button - Primary CTA */}
            <Button
              onClick={onRegister}
              startIcon={<PersonAdd sx={{ fontSize: 16 }} />}
              sx={{
                backgroundColor: '#ffffff',
                color: '#7c3aed',
                fontSize: '13px',
                fontWeight: 700,
                textTransform: 'none',
                padding: '6px 20px',
                borderRadius: '8px',
                border: '1px solid rgba(124, 58, 237, 0.1)',
                transition: 'all 0.2s ease',
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  backgroundColor: '#f9fafb',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              Create Account
            </Button>

            {/* Dismiss button */}
            {onDismiss && (
              <IconButton
                onClick={handleDismiss}
                size='small'
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  width: 32,
                  height: 32,
                  padding: 0,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: '#ffffff',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  },
                }}
              >
                <Close fontSize='small' />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    </Fade>
  )
}
